import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import {
  cancelReservation,
  checkReservationByReference,
  repayReservationFee,
} from "redux/features/reservations/reservationSlice";
import { socket } from "utils/socket";
import { ProfilClient } from "data/types";

export const usePaymentConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // URL parameters
  const queryParams = new URLSearchParams(location.search);
  const reference = queryParams.get("reservation");

  // Redux state
  const detailState = useAppSelector((state) => state.featureReservation.reservationDetail);
  const messageState = useAppSelector((state) => state.featureReservation.message);
  const userValidation = useAppSelector(
    (state) => (state.auth.user?.profil as ProfilClient)?.validation || ""
  ) as string;

  // Local state
  const [showSpinner, setShowSpinner] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Navigation handlers
  const handleExploreLocationsClick = () => {
    navigate("/espace-evenementiel");
  };

  const handleLocationsBack = () => {
    if (userValidation === "approuvé") {
      navigate("/client/my-reservations");
    } else {
      navigate("/client/evenements");
    }
  };

  // Payment actions
  const handleSubmit = async () => {
    if (!detailState?.id) return;

    setIsLoading(true);
    try {
      const data = await dispatch(repayReservationFee(detailState.id));
      console.log("Payment response data:", data);

      if (data.payload && typeof data.payload === "string" && data.payload !== "undefined") {
        window.location.href = data.payload;
      } else {
        console.error("Invalid payment URL received:", data.payload);
        // Optionally show an error message to the user
      }
    } catch (error) {
      console.error("Payment error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!detailState?.id) return;

    setShowModal(false);
    setLoading(true);

    try {
      await dispatch(cancelReservation({ reservation: detailState.id }));
      setCanceled(true);

      // Send socket notification for cancellation
      if (detailState?.payment_status === "requires_capture") {
        socket.emit("send-notification", {
          senderId: detailState.Client.userId,
          receiverId: detailState.Owner.userId,
          action: "cancel_reservation",
          reservationId: detailState.id,
          reservation: detailState,
        });
      }

      navigate("/client/evenements");
    } catch (error) {
      console.error("Cancellation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    setShowModal(true);
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (reference) {
          await dispatch(checkReservationByReference(reference));
          setShowSpinner(false);
        }
      } catch (error) {
        console.error("Error:", error);
        setShowSpinner(false);
      }
    };

    fetchData();
  }, [reference, dispatch]);

  // Socket notification for successful reservation
  useEffect(() => {
    if (detailState?.id && detailState?.payment_status === "requires_capture") {
      socket.emit("send-notification", {
        senderId: detailState.Client.userId,
        receiverId: detailState.Owner.userId,
        action: "create_reservation",
        reservationId: detailState.id,
        reservation: detailState,
      });
    }
  }, [detailState]);

  return {
    // State
    showSpinner,
    loading,
    isLoading,
    canceled,
    showModal,
    detailState,
    messageState,

    // Handlers
    handleExploreLocationsClick,
    handleLocationsBack,
    handleSubmit,
    handleCancel,
    handleClick,
    setShowModal,
  };
};
